import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import AdminLayout from "@/components/layouts/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  ArrowLeft, ExternalLink, Loader2, Power, Activity, Film, Users, Wallet, Save,
} from "lucide-react";
import { toast } from "sonner";
import { formatMoney } from "@/lib/money";

const HEX_RE = /^#[0-9A-Fa-f]{6}$/;

export default function AdminTenantDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: "", tagline: "", logo_url: "", theme_color: "", accent_color: "" });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/super-admin/tenants/${id}/detail`);
      setData(data);
      setForm({
        name: data.name || "",
        tagline: data.tagline || "",
        logo_url: data.logo_url || "",
        theme_color: data.theme_color || "#7C3AED",
        accent_color: data.accent_color || "#F59E0B",
      });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [id]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const saveBranding = async () => {
    if (!HEX_RE.test(form.theme_color) || !HEX_RE.test(form.accent_color)) {
      toast.error("Colors must be #RRGGBB hex");
      return;
    }
    setSaving(true);
    try {
      await api.patch(`/super-admin/tenants/${id}`, form);
      toast.success("Branding saved");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  const toggleStatus = async () => {
    if (!data) return;
    try {
      if (data.status === "active") {
        await api.delete(`/super-admin/tenants/${id}`);
        toast.success(`${data.name} suspended`);
      } else {
        await api.patch(`/super-admin/tenants/${id}`, { status: "active" });
        toast.success(`${data.name} reactivated`);
      }
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <AdminLayout>
      <div className="flex items-center gap-3 mb-6">
        <button onClick={() => navigate("/admin")} className="text-white/50 hover:text-white inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.3em]" data-testid="tenant-detail-back">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to control room
        </button>
      </div>

      {loading || !data ? (
        <div className="grid place-items-center py-20 text-white/40 text-sm uppercase tracking-widest" data-testid="tenant-detail-loading">
          <Loader2 className="w-5 h-5 animate-spin" />
        </div>
      ) : (
        <>
          <div className="flex items-end justify-between mb-8 gap-4 flex-wrap" data-testid="tenant-detail-header">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 border border-white/15 rounded-none" style={{ backgroundColor: data.theme_color }} />
              <div>
                <p className="text-[11px] uppercase tracking-[0.4em] text-white/40">Tenant · {data.plan}</p>
                <h1 className="mt-1 font-display font-bold text-3xl md:text-4xl tracking-tight">{data.name}</h1>
                <div className="text-white/55 text-sm mt-1 font-mono">/s/{data.slug}</div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Link to={`/s/${data.slug}`} target="_blank" rel="noreferrer" className="text-[10px] uppercase tracking-[0.3em] text-white/55 hover:text-white inline-flex items-center gap-1.5 border border-white/15 px-3 py-2" data-testid="tenant-detail-storefront">
                Storefront <ExternalLink className="w-3.5 h-3.5" />
              </Link>
              <Button onClick={toggleStatus} variant="ghost" className={`rounded-none border h-10 px-3 uppercase text-[11px] tracking-[0.25em] ${data.status === "active" ? "border-rose-400/30 text-rose-200 hover:text-rose-100" : "border-emerald-400/30 text-emerald-200 hover:text-emerald-100"}`} data-testid="tenant-detail-toggle-status">
                <Power className="w-4 h-4 mr-1.5" /> {data.status === "active" ? "Suspend" : "Reactivate"}
              </Button>
            </div>
          </div>

          {/* KPI strip */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8" data-testid="tenant-detail-kpis">
            <Kpi label="Content items" value={data.content_count} icon={<Film className="w-3.5 h-3.5" />} />
            <Kpi label="Subscribers" value={data.subscriber_count} icon={<Users className="w-3.5 h-3.5" />} />
            <Kpi label="Status" value={data.status} icon={<Activity className="w-3.5 h-3.5" />} accent={data.status === "active" ? "#10B981" : "#EF4444"} />
            <Kpi label="30d revenue" value={
              (data.revenue_30d_by_currency || []).length === 0 ? "—"
                : (data.revenue_30d_by_currency || []).map((r) => formatMoney(r.total, r.currency)).join(" / ")
            } icon={<Wallet className="w-3.5 h-3.5" />} />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Branding edit */}
            <div className="lg:col-span-2 bg-[#121212] border border-white/10 p-6" data-testid="tenant-detail-branding">
              <div className="flex items-end justify-between mb-5">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.3em] text-white/40">Branding</div>
                  <h3 className="font-display font-bold text-lg mt-1">Edit inline</h3>
                </div>
                <Button onClick={saveBranding} disabled={saving} className="rounded-none bg-white text-black hover:bg-amber-300 uppercase text-[11px] tracking-[0.25em]" data-testid="tenant-detail-save">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (<><Save className="w-3.5 h-3.5 mr-1.5" /> Save</>)}
                </Button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField label="Display name">
                  <Input value={form.name} onChange={(e) => set("name", e.target.value)} className="bg-transparent border-white/15 rounded-none focus-visible:border-white" data-testid="tenant-detail-name" />
                </FormField>
                <FormField label="Tagline">
                  <Input value={form.tagline} onChange={(e) => set("tagline", e.target.value)} className="bg-transparent border-white/15 rounded-none focus-visible:border-white" data-testid="tenant-detail-tagline" />
                </FormField>
                <FormField label="Logo URL" className="md:col-span-2">
                  <Input value={form.logo_url} onChange={(e) => set("logo_url", e.target.value)} className="bg-transparent border-white/15 rounded-none focus-visible:border-white font-mono text-sm" data-testid="tenant-detail-logo" />
                </FormField>
                <FormField label="Theme color">
                  <div className="flex items-center gap-2">
                    <input type="color" value={form.theme_color} onChange={(e) => set("theme_color", e.target.value)} className="w-12 h-10 bg-transparent border border-white/15" data-testid="tenant-detail-theme-picker" />
                    <Input value={form.theme_color} onChange={(e) => set("theme_color", e.target.value)} className="bg-transparent border-white/15 rounded-none focus-visible:border-white font-mono text-sm" data-testid="tenant-detail-theme-input" />
                  </div>
                </FormField>
                <FormField label="Accent color">
                  <div className="flex items-center gap-2">
                    <input type="color" value={form.accent_color} onChange={(e) => set("accent_color", e.target.value)} className="w-12 h-10 bg-transparent border border-white/15" data-testid="tenant-detail-accent-picker" />
                    <Input value={form.accent_color} onChange={(e) => set("accent_color", e.target.value)} className="bg-transparent border-white/15 rounded-none focus-visible:border-white font-mono text-sm" data-testid="tenant-detail-accent-input" />
                  </div>
                </FormField>
              </div>
            </div>

            {/* Owner + recent signups */}
            <div className="space-y-4">
              <div className="bg-[#121212] border border-white/10 p-5" data-testid="tenant-detail-owner">
                <div className="text-[10px] uppercase tracking-[0.3em] text-white/40">Owner</div>
                {data.owner ? (
                  <>
                    <div className="font-display font-bold text-base mt-2">{data.owner.full_name}</div>
                    <div className="text-white/55 text-sm">{data.owner.email}</div>
                  </>
                ) : (
                  <div className="text-white/45 text-sm mt-2">No owner</div>
                )}
              </div>
              <div className="bg-[#121212] border border-white/10 p-5" data-testid="tenant-detail-recent-signups">
                <div className="text-[10px] uppercase tracking-[0.3em] text-white/40">Recent signups</div>
                {data.recent_signups?.length === 0 ? (
                  <div className="text-white/45 text-sm mt-2">No subscribers yet</div>
                ) : (
                  <div className="mt-3 space-y-2">
                    {data.recent_signups.map((u, i) => (
                      <div key={i} className="text-xs flex items-center justify-between gap-2">
                        <span className="truncate">{u.email}</span>
                        <span className="text-white/35">{u.created_at ? new Date(u.created_at).toLocaleDateString() : ""}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="bg-[#121212] border border-white/10 p-5" data-testid="tenant-detail-activity">
                <div className="text-[10px] uppercase tracking-[0.3em] text-white/40">Last activity</div>
                <div className="text-white/70 text-sm mt-2">
                  {data.last_activity_at ? new Date(data.last_activity_at).toLocaleString() : "—"}
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </AdminLayout>
  );
}

function Kpi({ label, value, icon, accent }) {
  return (
    <div className="bg-[#121212] border border-white/10 p-5">
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-white/45">
        <span style={accent ? { color: accent } : {}}>{icon}</span>
        {label}
      </div>
      <div className="mt-3 font-display font-bold text-2xl tracking-tight uppercase">{value}</div>
    </div>
  );
}

function FormField({ label, children, className = "" }) {
  return (
    <div className={`space-y-1.5 ${className}`}>
      <Label className="text-[10px] uppercase tracking-[0.3em] text-white/50">{label}</Label>
      {children}
    </div>
  );
}
